import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import HelpIcon from '../components/HelpIcon';
import { MaterialTagForm } from '../components/MaterialTagForm';
import { ReceiveSheet } from '../components/ReceiveSheet';
import '../styles/site-office.css';
import { 
  ShoppingCart, Search, Plus, Package, Truck, DollarSign, Calendar, 
  Building, Store, MoreVertical, Edit2, Trash2, Eye, Send, Check, 
  Clock, RefreshCw, FileText, ChevronRight, AlertCircle, CheckCircle,
  XCircle, ArrowLeft, Sun, Moon
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-500', icon: FileText },
  pending: { label: 'Pending', color: 'bg-yellow-500', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-500', icon: Check },
  in_production: { label: 'In Production', color: 'bg-purple-500', icon: Package },
  shipped: { label: 'Shipped', color: 'bg-indigo-500', icon: Truck },
  partial_received: { label: 'Partial', color: 'bg-orange-500', icon: Package },
  received: { label: 'Received', color: 'bg-green-500', icon: CheckCircle },
  complete: { label: 'Complete', color: 'bg-green-600', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', icon: XCircle },
};

export default function PurchaseOrdersPage() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const vendorIdParam = searchParams.get('vendor');
  
  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [constants, setConstants] = useState({ statuses: [], payment_terms: [], shipping_methods: [] });
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialog states
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  // DP276-M1 — the material tags for this order, and the line being tagged.
  const [tags, setTags] = useState([]);
  // DP276-M5 — the read-only counts per line and for the order.
  const [summary, setSummary] = useState({});
  const [taggingItem, setTaggingItem] = useState(null);
  const [editTag, setEditTag] = useState(null);
  const [editSpot, setEditSpot] = useState('');
  const [openTags, setOpenTags] = useState(null);
  const [receivingItem, setReceivingItem] = useState(null);
  // PR-1 — the order line the Receive sheet is open on.
  const [receiveLine, setReceiveLine] = useState(null);
  
  // Form state
  const [orderForm, setOrderForm] = useState({
    vendor_id: vendorIdParam || '',
    project_id: projectId || '',
    required_date: '',
    shipping_method: '',
    shipping_address: '',
    shipping_instructions: '',
    payment_terms: '',
    internal_notes: '',
    vendor_notes: '',
    items: []
  });
  
  // New item form
  const [newItem, setNewItem] = useState({
    description: '',
    product_name: '',
    manufacturer: '',
    model_number: '',
    quantity: 1,
    unit: 'each',
    unit_price: 0
  });

  useEffect(() => {
    // Always fetch on mount or when projectId changes
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchOrders(),
          fetchVendors(),
          fetchProjects(),
          fetchConstants()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId]);

  useEffect(() => {
    if (vendorIdParam) {
      setOrderForm(prev => ({ ...prev, vendor_id: vendorIdParam }));
      setShowOrderDialog(true);
    }
  }, [vendorIdParam]);

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (projectId) params.append('project_id', projectId);
      const response = await axios.get(`${API}/api/purchase-orders?${params}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load purchase orders');
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/api/vendors`);
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/api/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchConstants = async () => {
    try {
      const response = await axios.get(`${API}/api/purchase-orders/constants`);
      setConstants(response.data);
    } catch (error) {
      console.error('Error fetching constants:', error);
    }
  };

  const openNewOrder = () => {
    setOrderForm({
      vendor_id: vendorIdParam || '',
      project_id: projectId || '',
      required_date: '',
      shipping_method: '',
      shipping_address: '',
      shipping_instructions: '',
      payment_terms: '',
      internal_notes: '',
      vendor_notes: '',
      items: []
    });
    setShowOrderDialog(true);
  };

  const addItemToOrder = () => {
    if (!newItem.description) {
      toast.error('Description required');
      return;
    }
    setOrderForm(prev => ({
      ...prev,
      items: [...prev.items, { ...newItem, id: Date.now().toString() }]
    }));
    setNewItem({
      description: '',
      product_name: '',
      manufacturer: '',
      model_number: '',
      quantity: 1,
      unit: 'each',
      unit_price: 0
    });
  };

  const removeItemFromOrder = (itemId) => {
    setOrderForm(prev => ({
      ...prev,
      items: prev.items.filter(i => i.id !== itemId)
    }));
  };

  const createOrder = async () => {
    if (!orderForm.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    if (orderForm.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      await axios.post(`${API}/api/purchase-orders`, orderForm);
      toast.success('Purchase order created');
      setShowOrderDialog(false);
      fetchOrders();
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create purchase order');
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/api/purchase-orders/${orderId}/status?status=${status}`);
      toast.success(`Status updated to ${STATUS_CONFIG[status]?.label || status}`);
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        const response = await axios.get(`${API}/api/purchase-orders/${orderId}`);
        setSelectedOrder(response.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Delete this purchase order?')) return;
    try {
      await axios.delete(`${API}/api/purchase-orders/${orderId}`);
      toast.success('Purchase order deleted');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setShowDetailSheet(false);
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete purchase order');
    }
  };

  const receiveItem = async (quantity) => {
    if (!selectedOrder || !receivingItem) return;
    try {
      await axios.put(`${API}/api/purchase-orders/${selectedOrder.id}/receive-item?item_id=${receivingItem.id}&quantity=${quantity}`);
      toast.success('Item received');
      setShowReceiveDialog(false);
      setReceivingItem(null);
      // Refresh order
      const response = await axios.get(`${API}/api/purchase-orders/${selectedOrder.id}`);
      setSelectedOrder(response.data);
      loadTags(response.data);
      fetchOrders();
    } catch (error) {
      console.error('Error receiving item:', error);
      toast.error('Failed to receive item');
    }
  };

  const viewOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowDetailSheet(true);
    loadTags(order);
  };

  // DP276-M1 — the tags already printed for this order's lines.
  const loadTags = async (order) => {
    if (!order?.project_id) { setTags([]); setSummary({}); return; }
    try {
      const r = await axios.get(
        `${API}/api/projects/${order.project_id}/material-tags?po_id=${order.id}`);
      setTags(Array.isArray(r.data) ? r.data : []);
    } catch { setTags([]); }
    // DP276-M5 — the counts the header and the strips read.
    try {
      const s = await axios.get(
        `${API}/api/projects/${order.project_id}/material-tags/summary?po_id=${order.id}`);
      setSummary(s.data || {});
    } catch { setSummary({}); }
  };

  const countsFor = (item) => (summary.items || {})[item.id]
    || { tagged: 0, on_site: 0, installed: 0, total: 0 };

  const tagsFor = (item) => tags.filter((t) => t.po_item_id === item.id);
  // DP277-R1 — the drawer scrolls now, so a short list of tags has no reason to
  // hide. A long one still opens on the toggle.
  const tagsShown = (item) => {
    if (openTags === item.id) return true;
    if (openTags === `off:${item.id}`) return false;
    const made = tagsFor(item).length;
    return made > 0 && made <= 5;
  };
  const tagsToMake = (item) => Math.max(
    0, Math.floor(item.quantity_received || 0) - tagsFor(item).length);
  const tagState = (item) => {
    // The furthest state any tag on this line has reached.
    const counts = countsFor(item);
    if (counts.installed > 0) return 'installed';
    if (counts.on_site > 0) return 'on_site';
    return counts.total > 0 ? 'tagged' : '';
  };

  const openTagging = async (item) => {
    let room = item.room || '';
    let location = '';
    let prefix = '';
    let title = item.product_name || item.name || item.description || '';
    if (item.clip_id) {
      try {
        const r = await axios.get(`${API}/api/clips/${item.clip_id}`);
        room = r.data?.room || room;
        location = r.data?.room_location || '';
        prefix = r.data?.spec_category || '';
        title = title || r.data?.title || '';
      } catch { /* the form asks for the room itself when the clip has none */ }
    }
    setTaggingItem({ ...item, _room: room, _location: location, _prefix: prefix, _title: title });
  };

  // DP276-M2 — correcting where one carton goes, and printing that one again.
  const saveSpot = async (tag) => {
    try {
      await axios.patch(
        `${API}/api/projects/${selectedOrder.project_id}/material-tags/${tag.id}`,
        { location: editSpot });
      setEditTag(null);
      loadTags(selectedOrder);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That change did not stick');
    }
  };

  const reprintTag = async (tag) => {
    try {
      const pdf = await axios.get(
        `${API}/api/projects/${selectedOrder.project_id}/material-tags/print.pdf?ids=${tag.id}&stock=ql62`,
        { responseType: 'blob' });
      window.open(URL.createObjectURL(pdf.data), '_blank');
    } catch { toast.error('That tag did not print'); }
  };

  // Filter orders
  const filteredOrders = orders.filter(o => {
    const matchesSearch = searchQuery === '' ||
      (o.po_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (o.vendor_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (o.project_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    
    // Tab filtering
    if (activeTab === 'pending') {
      return matchesSearch && ['pending', 'confirmed', 'in_production', 'shipped'].includes(o.status);
    }
    if (activeTab === 'received') {
      return matchesSearch && ['received', 'complete'].includes(o.status);
    }
    
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: orders.length,
    pending: orders.filter(o => ['pending', 'confirmed', 'in_production', 'shipped'].includes(o.status)).length,
    totalValue: orders.reduce((sum, o) => sum + (o.total || 0), 0),
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-[#0D0D0D]' : 'bg-gray-50'}`}>
      {/* Header Bar */}
      <div className={`border-b px-6 py-3 flex items-center justify-between ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white'}`}>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <ShoppingCart className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-2">
              <HelpIcon elementId="purchase-orders-page" position="inline" />
              <div>
                <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : ''}`}>Purchase Orders</h1>
                <p className="text-sm text-muted-foreground">
                  {projectId ? 'Project purchase orders' : 'All purchase orders'}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => {
            setDarkMode(!darkMode);
            document.documentElement.classList.toggle('dark');
          }}>
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </div>
      
      <main className="p-6 max-w-7xl mx-auto">
        {/* Action Bar */}
        <div className="flex items-center justify-end gap-2 mb-6">
          <Button variant="outline" onClick={fetchOrders}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={openNewOrder} data-testid="create-po-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create PO
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className={`relative ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
            <HelpIcon elementId="procurement-orders" position="top-right" />
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`relative ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
            <HelpIcon elementId="po-status-ordered" position="top-right" />
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`relative ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
            <HelpIcon elementId="report-committed" position="top-right" />
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalValue)}</p>
                  <p className="text-sm text-muted-foreground">Total Value</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs and Filters */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={darkMode ? 'bg-[#1A1A1A]' : ''}>
              <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
              <TabsTrigger value="pending">In Progress ({stats.pending})</TabsTrigger>
              <TabsTrigger value="received">Received</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search POs..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={`pl-9 w-48 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`w-40 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading purchase orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardContent className="py-12 text-center">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No Purchase Orders</h3>
              <p className="text-muted-foreground mb-4">Create your first purchase order to get started</p>
              <Button onClick={openNewOrder}>
                <Plus className="w-4 h-4 mr-2" />
                Create PO
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredOrders.map(order => {
              const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card 
                  key={order.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#3A3A3A]' : 'hover:border-gray-300'}`}
                  onClick={() => viewOrderDetail(order)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Status Icon */}
                        <div className={`w-10 h-10 rounded-lg ${statusConfig.color} bg-opacity-20 flex items-center justify-center`}>
                          <StatusIcon className={`w-5 h-5 ${statusConfig.color.replace('bg-', 'text-')}`} />
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-semibold ${darkMode ? 'text-white' : ''}`}>
                              {order.po_number}
                            </span>
                            <Badge variant="outline" className={`${statusConfig.color} bg-opacity-10 border-0`}>
                              {statusConfig.label}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Store className="w-3 h-3" />
                              {order.vendor_company || order.vendor_name}
                            </span>
                            {order.project_name && (
                              <span className="flex items-center gap-1">
                                <Building className="w-3 h-3" />
                                {order.project_name}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Package className="w-3 h-3" />
                              {order.items?.length || 0} items
                            </span>
                            {order.required_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Due: {order.required_date}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`font-semibold text-lg ${darkMode ? 'text-white' : ''}`}>
                            {formatCurrency(order.total)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {order.order_date}
                          </p>
                        </div>
                        
                        <div onClick={e => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => viewOrderDetail(order)}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {order.status === 'draft' && (
                                <DropdownMenuItem onClick={() => updateOrderStatus(order.id, 'pending')}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Send to Vendor
                                </DropdownMenuItem>
                              )}
                              {order.status === 'pending' && (
                                <DropdownMenuItem onClick={() => updateOrderStatus(order.id, 'confirmed')}>
                                  <Check className="w-4 h-4 mr-2" />
                                  Mark Confirmed
                                </DropdownMenuItem>
                              )}
                              {['confirmed', 'in_production', 'shipped'].includes(order.status) && (
                                <DropdownMenuItem onClick={() => updateOrderStatus(order.id, 'received')}>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Mark Received
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => deleteOrder(order.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create PO Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Create Purchase Order
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Vendor & Project */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor *</Label>
                <Select 
                  value={orderForm.vendor_id} 
                  onValueChange={v => setOrderForm(prev => ({ ...prev, vendor_id: v }))}
                >
                  <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.company || `${v.first_name} ${v.last_name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Project</Label>
                <Select 
                  value={orderForm.project_id || "none"} 
                  onValueChange={v => setOrderForm(prev => ({ ...prev, project_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                    <SelectValue placeholder="Select project (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name || p.project_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Dates & Shipping */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Required By</Label>
                <Input
                  type="date"
                  value={orderForm.required_date}
                  onChange={e => setOrderForm(prev => ({ ...prev, required_date: e.target.value }))}
                  className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                />
              </div>
              <div>
                <Label>Shipping Method</Label>
                <Select 
                  value={orderForm.shipping_method} 
                  onValueChange={v => setOrderForm(prev => ({ ...prev, shipping_method: v }))}
                >
                  <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {constants.shipping_methods?.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select 
                  value={orderForm.payment_terms} 
                  onValueChange={v => setOrderForm(prev => ({ ...prev, payment_terms: v }))}
                >
                  <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent>
                    {constants.payment_terms?.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Items */}
            <div className="space-y-3">
              <Label>Line Items</Label>
              
              {/* Add Item Form */}
              <div className={`p-3 rounded-lg border ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : 'bg-gray-50'}`}>
                <div className="grid grid-cols-6 gap-2">
                  <div className="col-span-2">
                    <Input
                      placeholder="Description *"
                      value={newItem.description}
                      onChange={e => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                      className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Manufacturer"
                      value={newItem.manufacturer}
                      onChange={e => setNewItem(prev => ({ ...prev, manufacturer: e.target.value }))}
                      className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={newItem.quantity}
                      onChange={e => setNewItem(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 1 }))}
                      className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Price"
                      value={newItem.unit_price}
                      onChange={e => setNewItem(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                      className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                    />
                  </div>
                  <div>
                    <Button onClick={addItemToOrder} className="w-full">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Items List */}
              {orderForm.items.length > 0 ? (
                <div className="space-y-2">
                  {orderForm.items.map((item, idx) => (
                    <div key={item.id} className={`flex items-center justify-between p-2 rounded ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
                      <div className="flex-1">
                        <span className="font-medium">{item.description}</span>
                        {item.manufacturer && <span className="text-sm text-muted-foreground ml-2">({item.manufacturer})</span>}
                      </div>
                      <div className="flex items-center gap-4">
                        <span>{item.quantity} x {formatCurrency(item.unit_price)}</span>
                        <span className="font-medium">{formatCurrency(item.quantity * item.unit_price)}</span>
                        <Button variant="ghost" size="icon" onClick={() => removeItemFromOrder(item.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t">
                    <span className="font-semibold">
                      Total: {formatCurrency(orderForm.items.reduce((sum, i) => sum + (i.quantity * i.unit_price), 0))}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">No items added yet</p>
              )}
            </div>
            
            {/* Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Internal Notes</Label>
                <Textarea
                  value={orderForm.internal_notes}
                  onChange={e => setOrderForm(prev => ({ ...prev, internal_notes: e.target.value }))}
                  placeholder="Internal notes (not sent to vendor)"
                  rows={2}
                  className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                />
              </div>
              <div>
                <Label>Vendor Notes</Label>
                <Textarea
                  value={orderForm.vendor_notes}
                  onChange={e => setOrderForm(prev => ({ ...prev, vendor_notes: e.target.value }))}
                  placeholder="Notes for vendor"
                  rows={2}
                  className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>Cancel</Button>
            <Button onClick={createOrder}>Create Purchase Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Sheet */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent className={`w-[600px] sm:w-[700px] max-w-full overflow-y-auto ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          {selectedOrder && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    {selectedOrder.po_number}
                  </span>
                  <Badge className={`${STATUS_CONFIG[selectedOrder.status]?.color} text-white`}>
                    {STATUS_CONFIG[selectedOrder.status]?.label}
                  </Badge>
                  {/* DP276-M5 — how much of this order is in the building. */}
                  {summary.order?.total > 0 ? (
                    <span className="kso-tag" data-testid="po-installed-count">
                      {summary.order.installed} of {summary.order.total} installed
                    </span>
                  ) : null}
                </SheetTitle>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                {/* Vendor & Project Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Vendor</p>
                    <p className="font-medium">{selectedOrder.vendor_company || selectedOrder.vendor_name}</p>
                    {selectedOrder.vendor_email && <p className="text-sm">{selectedOrder.vendor_email}</p>}
                  </div>
                  {selectedOrder.project_name && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Project</p>
                      <p className="font-medium">{selectedOrder.project_name}</p>
                    </div>
                  )}
                </div>
                
                {/* Dates */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Order Date</p>
                    <p>{selectedOrder.order_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Required By</p>
                    <p>{selectedOrder.required_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Est. Delivery</p>
                    <p>{selectedOrder.estimated_delivery || '-'}</p>
                  </div>
                </div>
                
                {/* Items */}
                <div>
                  <h4 className="font-medium mb-3">Line Items</h4>
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={item.id || idx} className={`p-3 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium">{item.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {item.manufacturer && `${item.manufacturer} `}
                              {item.model_number && `- ${item.model_number}`}
                            </p>
                          </div>
                          <div className="text-right">
                            <p>{item.quantity} x {formatCurrency(item.unit_price)}</p>
                            <p className="font-medium">{formatCurrency(item.net_price || item.quantity * item.unit_price)}</p>
                          </div>
                        </div>
                        
                        {/* Receiving status */}
                        {selectedOrder.status !== 'draft' && selectedOrder.status !== 'cancelled' && (
                          <div className="mt-2 pt-2 border-t flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm">
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <span>Received: {item.quantity_received || 0} / {item.quantity}</span>
                              {item.is_backordered && (
                                <Badge variant="destructive" className="text-xs">Backordered</Badge>
                              )}
                            </div>
                            {(item.quantity_received || 0) < item.quantity && (
                              /* PR-1 — the same receipt the clip page takes:
                                 count, where, location, condition. */
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setReceiveLine(item)}
                                data-testid={`po-line-receive-${item.id}`}
                              >
                                Receive
                              </Button>
                            )}
                          </div>
                        )}
                        {/* DP276-M1 — the tags for this line: what is printed,
                            where it stands, and one more run if more arrived. */}
                        {(item.quantity_received || 0) > 0 ? (
                          <div className="kmt-row kmt-tight" data-testid={`po-tags-${item.id}`}>
                            <span className="kmt-state">
                              {['tagged', 'on site', 'installed'].map((label) => (
                                <span key={label}
                                  className={tagState(item) === label.replace(' ', '_') ? 'on' : ''}>
                                  {label}
                                </span>
                              ))}
                            </span>
                            {countsFor(item).total > 0 ? (
                              <span className="m" data-testid={`po-item-installed-${item.id}`}>
                                {countsFor(item).installed}/{countsFor(item).total}
                              </span>
                            ) : null}
                            <button type="button" className="kso-btn"
                              onClick={() => setOpenTags(tagsShown(item) ? `off:${item.id}` : item.id)}
                              data-testid={`po-tags-toggle-${item.id}`}>{tagsFor(item).length
                                ? `${tagsFor(item).length} tag${tagsFor(item).length > 1 ? 's' : ''} made`
                                : 'no tags yet'}
                            </button>
                            <span className="m">{item.quantity_received} received</span>
                            <button type="button" className="kso-btn brand"
                              onClick={() => openTagging(item)}
                              data-testid={`po-print-tags-${item.id}`}>
                              {tagsToMake(item) > 0
                                ? `Print ${tagsToMake(item)} tag${tagsToMake(item) > 1 ? 's' : ''}`
                                : 'Reprint tags'}
                            </button>
                          </div>
                        ) : null}
                        {tagsShown(item) ? tagsFor(item).map((t) => (
                          <div className="kmt-row kmt-tight" key={t.id}
                            data-testid={`po-tag-${t.id}`}>
                            <span className="m">{t.count_index} of {t.count_total}</span>
                            {editTag === t.id ? (
                              <>
                                <input className="kso-in" value={editSpot}
                                  onChange={(e) => setEditSpot(e.target.value)}
                                  data-testid={`po-tag-spot-${t.id}`} />
                                <button type="button" className="kso-btn"
                                  onClick={() => saveSpot(t)}
                                  data-testid={`po-tag-save-${t.id}`}>Save</button>
                              </>
                            ) : (
                              <span className="n">{t.location || 'no location'} · {t.state.replace('_', ' ')}</span>
                            )}
                            {t.state !== 'installed' && editTag !== t.id ? (
                              <button type="button" className="kso-btn"
                                onClick={() => { setEditTag(t.id); setEditSpot(t.location || ''); }}
                                aria-label="Correct where this one goes"
                                data-testid={`po-tag-edit-${t.id}`}>Edit</button>
                            ) : null}
                            <button type="button" className="kso-btn"
                              onClick={() => reprintTag(t)}
                              data-testid={`po-tag-reprint-${t.id}`}>Reprint</button>
                          </div>
                        )) : null}
                        {taggingItem?.id === item.id ? (
                          <MaterialTagForm
                            projectId={selectedOrder.project_id}
                            source="po_item"
                            body={{ po_id: selectedOrder.id, po_item_id: item.id }}
                            title={taggingItem._title}
                            room={taggingItem._room}
                            location={taggingItem._location}
                            tradePrefix={taggingItem._prefix}
                            count={Math.max(1, tagsToMake(item) || 1)}
                            maxCount={Math.max(1, Math.floor(item.quantity_received || 1))}
                            onClose={() => setTaggingItem(null)}
                            onMade={() => loadTags(selectedOrder)}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Totals */}
                <div className="space-y-1 text-right">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(selectedOrder.subtotal)}</span>
                  </div>
                  {selectedOrder.tax_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax ({selectedOrder.tax_rate}%)</span>
                      <span>{formatCurrency(selectedOrder.tax_amount)}</span>
                    </div>
                  )}
                  {selectedOrder.shipping_cost > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Shipping</span>
                      <span>{formatCurrency(selectedOrder.shipping_cost)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                    <span>Total</span>
                    <span>{formatCurrency(selectedOrder.total)}</span>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  {selectedOrder.status === 'draft' && (
                    <Button className="flex-1" onClick={() => updateOrderStatus(selectedOrder.id, 'pending')}>
                      <Send className="w-4 h-4 mr-2" />
                      Send to Vendor
                    </Button>
                  )}
                  {selectedOrder.status === 'pending' && (
                    <Button className="flex-1" onClick={() => updateOrderStatus(selectedOrder.id, 'confirmed')}>
                      <Check className="w-4 h-4 mr-2" />
                      Mark Confirmed
                    </Button>
                  )}
                  {['confirmed', 'in_production', 'shipped', 'partial_received'].includes(selectedOrder.status) && (
                    <Button className="flex-1" onClick={() => updateOrderStatus(selectedOrder.id, 'received')}>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark All Received
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => deleteOrder(selectedOrder.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Receive Item Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
          <DialogHeader>
            <DialogTitle>Receive Item</DialogTitle>
          </DialogHeader>
          {receivingItem && (
            <div className="space-y-4">
              <p>{receivingItem.description}</p>
              <p className="text-sm text-muted-foreground">
                Ordered: {receivingItem.quantity} | Already Received: {receivingItem.quantity_received || 0}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => receiveItem(1)}
                >
                  Receive 1
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => receiveItem(receivingItem.quantity - (receivingItem.quantity_received || 0))}
                >
                  Receive All ({receivingItem.quantity - (receivingItem.quantity_received || 0)})
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PR-1 — Receive, from the order line. */}
      {receiveLine && selectedOrder ? (
        <ReceiveSheet
          clip={receiveLine.clip_id ? { id: receiveLine.clip_id,
            title: receiveLine.description || receiveLine.product_name,
            project_id: selectedOrder.project_id } : null}
          poId={selectedOrder.id}
          poItemId={receiveLine.id}
          of={Number(receiveLine.quantity) || 1}
          onSaved={fetchOrders}
          onClose={() => { setReceiveLine(null); fetchOrders(); }}
        />
      ) : null}
    </div>
  );
}
